import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { useToast } from "../../hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import {
  CreditCard,
  Plus,
  Edit,
  Trash2,
  Check,
  Settings,
  Clock,
  Calendar,
  Percent,
  Save,
  AlertCircle,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentPoliciesManagement() {
  const [policies, setPolicies] = useState([]);
  const [validityConfig, setValidityConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [showValidityDialog, setShowValidityDialog] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const [policyForm, setPolicyForm] = useState({
    code: "",
    name: "",
    description: "",
    payment_type: "comptant",
    payment_days: 0,
    document_text: "",
    is_default: false,
    is_active: true,
    requires_deposit: false,
    conditions: []
  });

  const [validityForm, setValidityForm] = useState({
    quote_validity_days: 30,
    invoice_payment_days: 5
  });

  const fetchPolicies = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/payment-policies/`, {
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      if (response.ok) {
        const data = await response.json();
        setPolicies(data);
      }
    } catch (error) {
      console.error("Error fetching policies:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les politiques de paiement",
        variant: "destructive"
      });
    }
  }, [toast]);

  const fetchValidityConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/payment-policies/config/validity`, {
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      if (response.ok) {
        const data = await response.json();
        setValidityConfig(data);
        setValidityForm({
          quote_validity_days: data.quote_validity_days,
          invoice_payment_days: data.invoice_payment_days
        });
      }
    } catch (error) {
      console.error("Error fetching validity config:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPolicies(), fetchValidityConfig()]);
      setLoading(false);
    };
    loadData();
  }, [fetchPolicies, fetchValidityConfig]);

  const handleAddPolicy = () => {
    setEditingPolicy(null);
    setPolicyForm({
      code: "",
      name: "",
      description: "",
      payment_type: "comptant",
      payment_days: 0,
      document_text: "",
      is_default: false,
      is_active: true,
      requires_deposit: false,
      conditions: []
    });
    setShowPolicyDialog(true);
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setPolicyForm({
      code: policy.code,
      name: policy.name,
      description: policy.description,
      payment_type: policy.payment_type,
      payment_days: policy.payment_days,
      document_text: policy.document_text,
      is_default: policy.is_default,
      is_active: policy.is_active,
      requires_deposit: policy.requires_deposit,
      conditions: policy.conditions || []
    });
    setShowPolicyDialog(true);
  };

  const handleSavePolicy = async () => {
    setSaving(true);
    try {
      const url = editingPolicy
        ? `${API_URL}/api/payment-policies/${editingPolicy.id}`
        : `${API_URL}/api/payment-policies/`;
      
      const method = editingPolicy ? "PUT" : "POST";
      
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", "Authorization": 'Bearer cookie-auth' },
        body: JSON.stringify(policyForm)
      });

      if (response.ok) {
        toast({
          title: "Succès",
          description: editingPolicy
            ? "Politique mise à jour avec succès"
            : "Politique créée avec succès"
        });
        setShowPolicyDialog(false);
        fetchPolicies();
      } else {
        const error = await response.json();
        toast({
          title: "Erreur",
          description: error.detail || "Erreur lors de l'enregistrement",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error saving policy:", error);
      toast({
        title: "Erreur",
        description: "Erreur lors de l'enregistrement",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePolicy = async (policy) => {
    if (!window.confirm(`Supprimer la politique "${policy.name}" ?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/payment-policies/${policy.id}`, {
        method: "DELETE"
      });

      if (response.ok) {
        toast({
          title: "Succès",
          description: "Politique supprimée"
        });
        fetchPolicies();
      } else {
        const error = await response.json();
        toast({
          title: "Erreur",
          description: error.detail || "Impossible de supprimer",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors de la suppression",
        variant: "destructive"
      });
    }
  };

  const handleSetDefault = async (policy) => {
    try {
      const response = await fetch(`${API_URL}/api/payment-policies/${policy.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": 'Bearer cookie-auth' },
        body: JSON.stringify({ is_default: true })
      });

      if (response.ok) {
        toast({
          title: "Succès",
          description: `"${policy.name}" est maintenant la politique par défaut`
        });
        fetchPolicies();
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors de la mise à jour",
        variant: "destructive"
      });
    }
  };

  const handleSaveValidity = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/payment-policies/config/validity`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": 'Bearer cookie-auth' },
        body: JSON.stringify(validityForm)
      });

      if (response.ok) {
        toast({
          title: "Succès",
          description: "Configuration mise à jour"
        });
        setShowValidityDialog(false);
        fetchValidityConfig();
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors de la mise à jour",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const getPaymentTypeLabel = (type) => {
    const labels = {
      comptant: "Paiement comptant",
      echeance: "Paiement à échéance",
      acompte: "Paiement par acompte",
      factoring: "Affacturage",
      custom: "Personnalisé"
    };
    return labels[type] || type;
  };

  const getPaymentTypeColor = (type) => {
    const colors = {
      comptant: "bg-green-100 text-green-800",
      echeance: "bg-blue-100 text-blue-800",
      acompte: "bg-amber-100 text-amber-800",
      factoring: "bg-purple-100 text-purple-800",
      custom: "bg-gray-100 text-gray-800"
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1db584]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900">Politiques de paiement</h1>
          <p className="text-gray-500 mt-1">
            Gérez les modalités de paiement pour les devis et factures
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowValidityDialog(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Délais de validité
          </Button>
          <Button onClick={handleAddPolicy} className="bg-[#1db584] hover:bg-[#19a074]">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle politique
          </Button>
        </div>
      </div>

      {/* Config Card */}
      {validityConfig && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-blue-800">
                    <strong>Validité devis :</strong> {validityConfig.quote_validity_days} jours
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-blue-800">
                    <strong>Délai paiement facture :</strong> {validityConfig.invoice_payment_days} jours
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowValidityDialog(true)}
                className="text-blue-600 hover:text-blue-800"
              >
                <Edit className="w-4 h-4 mr-1" />
                Modifier
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Policies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {policies.map((policy) => (
          <Card
            key={policy.id}
            className={`relative ${!policy.is_active ? "opacity-60" : ""} ${
              policy.is_default ? "ring-2 ring-[#1db584]" : ""
            }`}
          >
            {policy.is_default && (
              <div className="absolute -top-2 -right-2">
                <Badge className="bg-[#1db584]">
                  <Check className="w-3 h-3 mr-1" />
                  Par défaut
                </Badge>
              </div>
            )}
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg">{policy.name}</CardTitle>
                  <CardDescription className="mt-1">
                    Code: <span className="font-mono text-xs">{policy.code}</span>
                  </CardDescription>
                </div>
                <Badge className={getPaymentTypeColor(policy.payment_type)}>
                  {getPaymentTypeLabel(policy.payment_type)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                {policy.description}
              </p>
              
              {policy.payment_type === "echeance" && policy.payment_days > 0 && (
                <div className="flex items-center gap-2 text-sm text-blue-600 mb-2">
                  <Clock className="w-4 h-4" />
                  <span>Délai: {policy.payment_days} jours</span>
                </div>
              )}

              {policy.payment_type === "acompte" && policy.conditions?.length > 0 && (
                <div className="mb-2 p-2 bg-amber-50 rounded text-xs">
                  <div className="font-medium text-amber-800 mb-1">Échéancier:</div>
                  {policy.conditions.map((c, idx) => (
                    <div key={idx} className="text-amber-700">
                      • {c.description}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2 mt-3">
                {!policy.is_default && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSetDefault(policy)}
                  >
                    Définir par défaut
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditPolicy(policy)}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                {!policy.is_default && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-800"
                    onClick={() => handleDeletePolicy(policy)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Policy Dialog */}
      <Dialog open={showPolicyDialog} onOpenChange={setShowPolicyDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPolicy ? "Modifier la politique" : "Nouvelle politique de paiement"}
            </DialogTitle>
            <DialogDescription>
              Configurez les conditions de paiement pour vos documents
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="code">Code unique *</Label>
                <Input
                  id="code"
                  value={policyForm.code}
                  onChange={(e) => setPolicyForm({ ...policyForm, code: e.target.value.toUpperCase() })}
                  placeholder="EX: NET_30"
                  disabled={!!editingPolicy}
                />
              </div>
              <div>
                <Label htmlFor="name">Nom *</Label>
                <Input
                  id="name"
                  value={policyForm.name}
                  onChange={(e) => setPolicyForm({ ...policyForm, name: e.target.value })}
                  placeholder="Paiement à 30 jours"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="payment_type">Type de paiement *</Label>
              <Select
                value={policyForm.payment_type}
                onValueChange={(value) => setPolicyForm({
                  ...policyForm,
                  payment_type: value,
                  requires_deposit: value === "acompte"
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="comptant">Paiement comptant</SelectItem>
                  <SelectItem value="echeance">Paiement à échéance</SelectItem>
                  <SelectItem value="acompte">Paiement par acompte</SelectItem>
                  <SelectItem value="factoring">Affacturage</SelectItem>
                  <SelectItem value="custom">Personnalisé</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {policyForm.payment_type === "echeance" && (
              <div>
                <Label htmlFor="payment_days">Délai de paiement (jours)</Label>
                <Input
                  id="payment_days"
                  type="number"
                  min="0"
                  value={policyForm.payment_days}
                  onChange={(e) => setPolicyForm({ ...policyForm, payment_days: parseInt(e.target.value) || 0 })}
                />
              </div>
            )}

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={policyForm.description}
                onChange={(e) => setPolicyForm({ ...policyForm, description: e.target.value })}
                placeholder="Description courte de la politique..."
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="document_text">Texte affiché sur les documents *</Label>
              <Textarea
                id="document_text"
                value={policyForm.document_text}
                onChange={(e) => setPolicyForm({ ...policyForm, document_text: e.target.value })}
                placeholder="Ce texte apparaîtra sur les devis et factures..."
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Switch
                  checked={policyForm.is_active}
                  onCheckedChange={(checked) => setPolicyForm({ ...policyForm, is_active: checked })}
                />
                <Label>Politique active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={policyForm.is_default}
                  onCheckedChange={(checked) => setPolicyForm({ ...policyForm, is_default: checked })}
                />
                <Label>Définir par défaut</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPolicyDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSavePolicy}
              disabled={saving || !policyForm.code || !policyForm.name || !policyForm.document_text}
              className="bg-[#1db584] hover:bg-[#19a074]"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {editingPolicy ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validity Config Dialog */}
      <Dialog open={showValidityDialog} onOpenChange={setShowValidityDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configuration des délais</DialogTitle>
            <DialogDescription>
              Définissez les délais par défaut pour vos documents
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 text-blue-800 mb-2">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Ces valeurs sont utilisées par défaut</span>
              </div>
              <p className="text-xs text-blue-600">
                Vous pouvez les modifier lors de la génération de chaque document
              </p>
            </div>

            <div>
              <Label htmlFor="quote_validity">Validité des devis (jours)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  id="quote_validity"
                  type="number"
                  min="1"
                  max="365"
                  value={validityForm.quote_validity_days}
                  onChange={(e) => setValidityForm({
                    ...validityForm,
                    quote_validity_days: parseInt(e.target.value) || 30
                  })}
                  className="w-24"
                />
                <span className="text-sm text-gray-500">jours après émission</span>
              </div>
            </div>

            <div>
              <Label htmlFor="invoice_payment">Délai de paiement des factures (jours)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  id="invoice_payment"
                  type="number"
                  min="0"
                  max="365"
                  value={validityForm.invoice_payment_days}
                  onChange={(e) => setValidityForm({
                    ...validityForm,
                    invoice_payment_days: parseInt(e.target.value) || 5
                  })}
                  className="w-24"
                />
                <span className="text-sm text-gray-500">jours après émission</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowValidityDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSaveValidity}
              disabled={saving}
              className="bg-[#1db584] hover:bg-[#19a074]"
            >
              {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
